import { URIObject } from '@imago/api-client'
import S from 'stripe'

export const StripeCardError = S.errors.StripeCardError
export const StripeRateLimitError = S.errors.StripeRateLimitError

const Stripe_ID__Brand: unique symbol = Symbol()
export type Stripe_ID = string & {[Stripe_ID__Brand]: never}

export type Stripe_CustomerID = `cus_${string}` & Stripe_ID
export type Stripe_ProductID = `prod_${string}` & Stripe_ID
export type Stripe_PriceID = `price_${string}` & Stripe_ID
export type Stripe_PaymentMethodConfigurationID = `pmc_${string}` & Stripe_ID
export type Stripe_SubscriptionID = `sub_${string}` & Stripe_ID
export type Stripe_InvoiceID = `in_${string}` & Stripe_ID
export type Stripe_PaymentIntentID = `pi_${string}` & Stripe_ID
export type Stripe_PaymentMethodID = `pm_${string}` & Stripe_ID
export type Stripe_ChargeID = (`ch_${string}` | `py_${string}`) & Stripe_ID

export function Stripe_isPaymentIntentID(ID: Stripe_ID): ID is Stripe_PaymentIntentID {
	return ID.startsWith('pi_')
}

interface Stripe_Customer_InvoiceSettingsBase extends S.Customer.InvoiceSettings {
	default_payment_method: Stripe_PaymentMethodID | S.PaymentMethod | null
}

interface Stripe_Customer_InvoiceSettings extends Stripe_Customer_InvoiceSettingsBase {
	default_payment_method: Stripe_PaymentMethodID | null
}

interface Stripe_CustomerBase extends S.Customer {
	id: Stripe_CustomerID
	invoice_settings: Stripe_Customer_InvoiceSettingsBase
}

export interface Stripe_Customer extends Stripe_CustomerBase {
	invoice_settings: Stripe_Customer_InvoiceSettings
}

interface Stripe_ChargeBase extends S.Charge {
	id: Stripe_ChargeID
}

export interface Stripe_Charge extends Stripe_ChargeBase {
	payment_intent: Stripe_PaymentIntentID
}

interface Stripe_PaymentIntentBase extends S.PaymentIntent {
	id: Stripe_PaymentIntentID
	customer: Stripe_CustomerID | Stripe_Customer | null
	latest_charge: Stripe_ChargeID | Stripe_Charge | null
}

export interface Stripe_PaymentIntent extends Stripe_PaymentIntentBase {
	customer: Stripe_CustomerID | null
	latest_charge: Stripe_ChargeID | null
}

export interface Stripe_PaymentIntentWithLatestCharge extends Stripe_PaymentIntentBase {
	customer: Stripe_CustomerID | null
	latest_charge: Stripe_Charge | null
}

interface Stripe_SubscriptionBase extends S.Subscription {
	id: Stripe_SubscriptionID
}

export interface Stripe_Subscription extends Stripe_SubscriptionBase {
	customer: Stripe_CustomerID
	default_payment_method: Stripe_PaymentMethodID | null
}

interface Stripe_InvoiceBase extends S.Invoice {
	id: Stripe_InvoiceID
}

interface Stripe_InvoicePayment_Payment extends S.InvoicePayment.Payment {
	payment_intent: Stripe_PaymentIntentID
}

export interface Stripe_InvoicePayment extends S.InvoicePayment {
	payment: Stripe_InvoicePayment_Payment
}

export interface Stripe_Invoice extends Stripe_InvoiceBase {
	customer: Stripe_CustomerID
	payments: S.ApiList<Stripe_InvoicePayment>
}

export interface Stripe_CheckoutSession extends S.Checkout.Session {
	customer: Stripe_CustomerID
}

interface SubscriptionUpdateParams extends Omit<S.SubscriptionUpdateParams, 'default_payment_method'> {
	default_payment_method?: Stripe_PaymentMethodID | null
}

export function Stripe_SSQL_quote(value: string) {
	return `"${value.replaceAll('"', '\"')}"`
}

export class StripeRemote {
	private uri: URIObject

	constructor(uri: URIObject) {
		this.uri = uri
	}

	get stripe() { return new S(this.uri.password!) }
	get publishableKey() { return this.uri.username! }
	get paymentMethodConfigurationID() {
		return this.uri.queryParams.get('payment_method_configuration') as (Stripe_PaymentMethodConfigurationID | null) ?? undefined
	}
}

export async function Stripe_Customers_list(stripe: StripeRemote) {
	return await getWholeList(x => stripe.stripe.customers.list(x)) as Stripe_Customer[]
}

export async function Stripe_Customers_get(stripe: StripeRemote, id: Stripe_CustomerID) {
	return await stripe.stripe.customers.retrieve(id, {expand: ['test_clock']}) as unknown as Stripe_Customer
}

export async function Stripe_Customers_update(stripe: StripeRemote, id: Stripe_CustomerID, info: Omit<S.CustomerUpdateParams, 'expand'>) {
	return await stripe.stripe.customers.update(id, {...info, expand: ['test_clock']}) as unknown as Stripe_Customer
}

export async function Stripe_Subscriptions_list(stripe: StripeRemote, {limit, ...params}: Omit<S.SubscriptionListParams, keyof S.PaginationParams | 'expand'> & {limit: number}) {
	return await getList(x => stripe.stripe.subscriptions.list({...x, ...params}), limit) as Stripe_Subscription[]
}

export async function Stripe_Subscriptions_update(stripe: StripeRemote, id: Stripe_SubscriptionID, info: Omit<SubscriptionUpdateParams, 'expand'>) {
	return await stripe.stripe.subscriptions.update(id, info as S.SubscriptionUpdateParams) as unknown as Stripe_Subscription
}

export async function Stripe_Subscriptions_cancel(stripe: StripeRemote, id: Stripe_SubscriptionID, info: Omit<S.SubscriptionCancelParams, 'expand'>) {
	return await stripe.stripe.subscriptions.cancel(id, info as S.SubscriptionCancelParams) as unknown as Stripe_Subscription
}

export async function Stripe_Invoices_list(stripe: StripeRemote, {limit, ...params}: Omit<S.InvoiceListParams, keyof S.PaginationParams | 'expand'> & {limit: number}) {
	return await getList(x => stripe.stripe.invoices.list({...x, ...params, expand: ['data.payments']}), limit) as Stripe_Invoice[]
}

export async function Stripe_Invoices_void(stripe: StripeRemote, id: Stripe_InvoiceID, params: Omit<S.InvoiceVoidInvoiceParams, 'expand'> = {}) {
	return await stripe.stripe.invoices.voidInvoice(id, {...params, expand: ['payments']}) as unknown as Stripe_Invoice
}

export async function Stripe_PaymentIntents_listWithLatestCharge(stripe: StripeRemote, {limit, ...params}: Omit<S.PaymentIntentListParams, keyof S.PaginationParams | 'expand'> & {limit: number}) {
	return await getList(x => stripe.stripe.paymentIntents.list({...x, ...params, expand: ['data.latest_charge']}), limit) as Stripe_PaymentIntentWithLatestCharge[]
}

export async function Stripe_PaymentIntents_searchWithLatestCharge(stripe: StripeRemote, {limit, ...params}: Omit<S.PaymentIntentSearchParams, keyof S.PaginationParams | 'expand'> & {limit: number}) {
	return await getList(x => stripe.stripe.paymentIntents.search({...x, ...params, expand: ['data.latest_charge']}), limit) as Stripe_PaymentIntentWithLatestCharge[]
}

export async function Stripe_PaymentIntents_update(stripe: StripeRemote, id: Stripe_PaymentIntentID, info: Omit<S.PaymentIntentUpdateParams, 'expand'>) {
	return await stripe.stripe.paymentIntents.update(id, info) as unknown as Stripe_PaymentIntent
}

export async function Stripe_CheckoutSessions_list(stripe: StripeRemote) {
	return await getWholeList(x => stripe.stripe.checkout.sessions.list({...x, expand: ['data.line_items']})) as Stripe_CheckoutSession[]
}

async function getList<T>(list: (l: S.PaginationParams) => S.ApiListPromise<T> | S.ApiSearchResultPromise<T>, limit: number) {
	if (limit === 1/0) {
		return await getWholeList(list)
	} else {
		return (await list({limit})).data
	}
}

async function getWholeList<T>(list: (l: S.PaginationParams) => S.ApiListPromise<T> | S.ApiSearchResultPromise<T>) {
	const items = new Array<T>()
	let i = 0
	for await (const item of list({limit: 100})) {
		items.push(item)
		++i
		if (i % 100 === 0) {console.debug(i)}
	}
	return items
}

export interface CS_Customer<Metadata> extends Omit<Stripe_Customer, 'metadata'> {
	kind: 'Customer'
	metadata: Metadata
	test_clock?: S.TestHelpers.TestClock | null
}

export function CS_castCustomer<Metadata>(c: Stripe_Customer): CS_Customer<Metadata> {
	return {
		...c,
		kind: 'Customer',
		metadata: c.metadata as Metadata,
		test_clock: c.test_clock as S.TestHelpers.TestClock | null | undefined,
	}
}

export interface CS_Subscription<Metadata> extends Omit<Stripe_Subscription, 'customer' | 'metadata'> {
	kind: 'Subscription'
	customer: unknown
	recorded_customer_id: Stripe_CustomerID
	metadata: Metadata
}

export function CS_castSubscription<Metadata>(s: Stripe_Subscription): CS_Subscription<Metadata> {
	return {
		...s,
		kind: 'Subscription',
		customer: undefined,
		recorded_customer_id: s.customer,
		metadata: s.metadata as Metadata,
	}
}

export interface CS_Invoice<Metadata> extends Omit<Stripe_Invoice, 'customer' | 'metadata'> {
	kind: 'Invoice'
	customer: unknown
	recorded_customer_id: Stripe_CustomerID
	metadata: Metadata
}

export function CS_castInvoice<Metadata>(i: Stripe_Invoice): CS_Invoice<Metadata> {
	return {
		...i,
		kind: 'Invoice',
		customer: undefined,
		recorded_customer_id: i.customer,
		metadata: i.metadata as Metadata,
	}
}

export interface CS_PaymentIntent<Metadata> extends Omit<Stripe_PaymentIntentWithLatestCharge, 'customer' | 'metadata'> {
	kind: 'PaymentIntent'
	customer: unknown
	recorded_customer_id: Stripe_CustomerID | null
	metadata: Metadata
}

export function CS_castPaymentIntent<Metadata>(p: Stripe_PaymentIntentWithLatestCharge): CS_PaymentIntent<Metadata> {
	return {
		...p,
		kind: 'PaymentIntent',
		customer: undefined,
		recorded_customer_id: p.customer,
		metadata: p.metadata as Metadata,
	}
}
